import type { PrintTemplateBlock, PrintTemplateDefinition, PrintTemplateType } from './types.ts'

export const SALE_TEMPLATE_SLOTS = ['top', 'after_issuer', 'after_document', 'before_items', 'after_items', 'after_totals', 'bottom'] as const
export type SaleTemplateSlot = typeof SALE_TEMPLATE_SLOTS[number]

export const SALE_MANDATORY_GROUPS = Object.freeze({
  fiscal: ['fiscal-qr', 'fiscal-verifactu-legend', 'fiscal-top-gap'],
  issuer: ['venue-name', 'venue-legal-name', 'venue-tax-id', 'venue-address'],
  document: ['invoice-heading-gap', 'invoice-heading', 'copy-label-gap', 'copy-label', 'header-gap', 'ticket-number', 'ticket-date'],
  items: ['items-gap', 'items-title', 'items-separator', 'items'],
  totals: ['totals-gap', 'totals-separator', 'totals'],
} as const)

const saleBlocks: PrintTemplateBlock[] = [
  { id: 'fiscal-qr', type: 'qr', value: '{{fiscal.verification_url}}', when: 'fiscal.show_qr' },
  { id: 'fiscal-verifactu-legend', type: 'text', value: 'VERI*FACTU', align: 'center', when: 'fiscal.show_verifactu_legend' },
  { id: 'fiscal-top-gap', type: 'spacer', when: 'fiscal.show_qr' },
  { id: 'venue-name', type: 'text', value: '{{venue.name}}', align: 'center' },
  { id: 'venue-legal-name', type: 'text', value: '{{venue.legal_name}}', align: 'center', when: 'venue.legal_name' },
  { id: 'venue-tax-id', type: 'text', value: 'NIF/CIF {{venue.tax_id}}', align: 'center', when: 'venue.tax_id' },
  { id: 'venue-address', type: 'text', value: '{{venue.address}}', align: 'center', when: 'venue.address' },
  { id: 'invoice-heading-gap', type: 'spacer', when: 'document.title' },
  { id: 'invoice-heading', type: 'text', value: '{{document.title}}', align: 'center', when: 'document.title', unless: 'ticket.is_rectificative' },
  { id: 'copy-label-gap', type: 'spacer', when: 'document.label' },
  { id: 'copy-label', type: 'text', value: '{{document.label}}', align: 'center', when: 'document.label' },
  { id: 'header-gap', type: 'spacer' },
  { id: 'ticket-number', type: 'row', label: '{{document.number_label}}', value: '{{ticket.number}}' },
  { id: 'ticket-date', type: 'row', label: '{{document.date_label}}', value: '{{ticket.datetime}}' },
  { id: 'rectified-document', type: 'text', value: 'FACTURA RECTIFICATIVA', align: 'center', when: 'ticket.is_rectificative' },
  { id: 'rectified-invoice', type: 'row', label: 'Rectifica factura', value: '{{ticket.rectified_invoice}}', when: 'ticket.rectified_invoice' },
  { id: 'rectified-date', type: 'row', label: 'Fecha factura original', value: '{{ticket.rectified_invoice_date}}', when: 'ticket.rectified_invoice_date' },
  { id: 'cash-register', type: 'row', label: 'Caja', value: '{{cash_register.name}}', when: 'cash_register.name' },
  { id: 'employee', type: 'row', label: 'Empleado', value: '{{employee.name}}', when: 'employee.name' },
  { id: 'customer-gap', type: 'spacer', when: 'customer.name' },
  { id: 'customer-title', type: 'text', value: 'CLIENTE', when: 'customer.name' },
  { id: 'customer-separator', type: 'separator', when: 'customer.name' },
  { id: 'customer-name', type: 'text', value: '{{customer.name}}', when: 'customer.name' },
  { id: 'customer-tax-id', type: 'text', value: '{{customer.tax_id}}', when: 'customer.tax_id' },
  { id: 'customer-address', type: 'text', value: '{{customer.address}}', when: 'customer.address' },
  { id: 'customer-city', type: 'text', value: '{{customer.postal_city}}', when: 'customer.postal_city' },
  { id: 'customer-province', type: 'text', value: '{{customer.province}}', when: 'customer.province' },
  { id: 'customer-country', type: 'text', value: '{{customer.country}}', when: 'customer.show_country' },
  { id: 'items-gap', type: 'spacer' },
  { id: 'items-title', type: 'text', value: 'PRODUCTOS' },
  { id: 'items-separator', type: 'separator' },
  { id: 'items', type: 'repeat', source: 'items', blocks: [
    { id: 'item-row', type: 'row', label: '{{quantity}} x {{name}}', value: '{{total}}' },
    { id: 'item-details', type: 'repeat', source: 'details', blocks: [
      { id: 'item-detail', type: 'text', value: '{{text}}' },
    ] },
  ] },
  { id: 'totals-gap', type: 'spacer' },
  { id: 'totals-separator', type: 'separator' },
  { id: 'totals', type: 'repeat', source: 'totals.rows', blocks: [
    { id: 'total-row', type: 'row', label: '{{label}}', value: '{{value}}', bold: false },
  ] },
  { id: 'payment-gap', type: 'spacer', when: 'payment.rows' },
  { id: 'payment-title', type: 'text', value: 'PAGO', when: 'payment.rows' },
  { id: 'payment-separator', type: 'separator', when: 'payment.rows' },
  { id: 'payments', type: 'repeat', source: 'payment.rows', blocks: [
    { id: 'payment-row', type: 'row', label: '{{label}}', value: '{{value}}' },
  ] },
  { id: 'fiscal-gap', type: 'spacer', when: 'fiscal.title' },
  { id: 'fiscal-title', type: 'text', value: '{{fiscal.title}}', when: 'fiscal.title' },
  { id: 'fiscal-separator', type: 'separator', when: 'fiscal.title' },
  { id: 'fiscal-code', type: 'text', value: 'Código: {{fiscal.external_code}}', when: 'fiscal.external_code' },
  { id: 'fiscal-url', type: 'text', value: '{{fiscal.verification_url}}', when: 'fiscal.show_url' },
  { id: 'fiscal-unavailable', type: 'text', value: 'QR no disponible.', when: 'fiscal.error' },
  { id: 'fiscal-error', type: 'text', value: 'Motivo: {{fiscal.error}}', when: 'fiscal.error' },
  { id: 'footer-gap', type: 'spacer', when: 'footer.text' },
  { id: 'footer', type: 'text', value: '{{footer.text}}', align: 'center', when: 'footer.text' },
  { id: 'end-space', type: 'spacer', lines: 2 },
]

const cashClosureBlocks: PrintTemplateBlock[] = [
  { id: 'closure-title', type: 'text', value: '{{document.title}}', align: 'center' },
  { id: 'closure-venue', type: 'text', value: '{{venue.name}}', align: 'center' },
  { id: 'closure-legal', type: 'text', value: '{{venue.legal_name}}', align: 'center', when: 'venue.legal_name' },
  { id: 'closure-tax', type: 'text', value: 'NIF/CIF {{venue.tax_id}}', align: 'center', when: 'venue.tax_id' },
  { id: 'closure-address', type: 'text', value: '{{venue.address}}', align: 'center', when: 'venue.address' },
  { id: 'closure-copy-gap', type: 'spacer', when: 'document.label' },
  { id: 'closure-copy', type: 'text', value: '{{document.label}}', align: 'center', when: 'document.label' },
  { id: 'closure-header-gap', type: 'spacer' },
  { id: 'closure-register', type: 'row', label: 'Caja', value: '{{cash_register.name}}' },
  { id: 'closure-session', type: 'row', label: 'Turno', value: '{{cash_session.number}}' },
  { id: 'closure-date', type: 'row', label: 'Cierre', value: '{{ticket.datetime}}' },
  { id: 'closure-employee', type: 'row', label: 'Empleado', value: '{{employee.name}}', when: 'employee.name' },
  { id: 'closure-opened', type: 'row', label: 'Apertura', value: '{{cash_session.opened_at}}', when: 'cash_session.show_times' },
  { id: 'closure-closed', type: 'row', label: 'Cierre', value: '{{cash_session.closed_at}}', when: 'cash_session.show_times' },
  { id: 'summary-gap', type: 'spacer' },
  { id: 'summary-title', type: 'text', value: 'RESUMEN' },
  { id: 'summary-separator', type: 'separator' },
  { id: 'summary-rows', type: 'repeat', source: 'summary.rows', blocks: [{ id: 'summary-row', type: 'row', label: '{{label}}', value: '{{value}}' }] },
  { id: 'payments-gap', type: 'spacer', when: 'payment.rows' },
  { id: 'payments-title', type: 'text', value: 'MÉTODOS DE PAGO', when: 'payment.rows' },
  { id: 'payments-separator', type: 'separator', when: 'payment.rows' },
  { id: 'closure-payments', type: 'repeat', source: 'payment.rows', blocks: [{ id: 'closure-payment', type: 'row', label: '{{label}}', value: '{{value}}' }] },
  { id: 'cash-gap', type: 'spacer' },
  { id: 'cash-title', type: 'text', value: 'EFECTIVO' },
  { id: 'cash-separator', type: 'separator' },
  { id: 'cash-rows', type: 'repeat', source: 'cash.rows', blocks: [{ id: 'cash-row', type: 'row', label: '{{label}}', value: '{{value}}' }] },
  { id: 'operations-gap', type: 'spacer' },
  { id: 'operations-title', type: 'text', value: 'OPERATIVA' },
  { id: 'operations-separator', type: 'separator' },
  { id: 'operations-rows', type: 'repeat', source: 'operations.rows', blocks: [{ id: 'operations-row', type: 'row', label: '{{label}}', value: '{{value}}' }] },
  { id: 'closure-footer-gap', type: 'spacer' },
  { id: 'closure-id', type: 'row', label: 'ID cierre', value: '{{document.id}}' },
  { id: 'closure-generated', type: 'row', label: 'Generado', value: '{{document.generated_at}}' },
  { id: 'closure-complete-gap', type: 'spacer' },
  { id: 'closure-complete', type: 'text', value: 'CIERRE COMPLETADO', align: 'center' },
  { id: 'closure-end', type: 'spacer', lines: 2 },
]

const productionBlocks: PrintTemplateBlock[] = [
  { id: 'production-top', type: 'separator' },
  { id: 'production-table', type: 'text', value: 'MESA {{table.name}}', align: 'center', bold: true, size: 'large' },
  { id: 'production-order', type: 'text', value: 'ENVÍO #{{order.number}}', align: 'center', bold: true },
  { id: 'production-time', type: 'text', value: '{{ticket.time}}', align: 'center' },
  { id: 'production-head-separator', type: 'separator' },
  { id: 'production-gap', type: 'spacer' },
  { id: 'destinations', type: 'repeat', source: 'destinations', blocks: [
    { id: 'destination-title', type: 'text', value: '--- {{name}} ---', align: 'center', bold: true },
    { id: 'destination-items', type: 'repeat', source: 'items', blocks: [
      { id: 'production-item', type: 'text', value: '{{quantity}}x {{name}}', bold: true },
      { id: 'production-item-details', type: 'repeat', source: 'details', blocks: [{ id: 'production-detail', type: 'text', value: '{{text}}' }] },
    ] },
    { id: 'destination-gap', type: 'spacer' },
  ] },
  { id: 'production-bottom', type: 'separator' },
]

export const DEFAULT_PRINT_TEMPLATES: Record<PrintTemplateType, PrintTemplateDefinition> = {
  simplified_invoice: { version: 1, blocks: saleBlocks },
  invoice: { version: 1, blocks: saleBlocks },
  cash_closure: { version: 1, blocks: cashClosureBlocks },
  production: { version: 1, blocks: productionBlocks },
  kds: { version: 1, blocks: [
    { id: 'kds-order', type: 'text', value: 'COMANDA #{{order.number}}', bold: true, size: 'large' },
    { id: 'kds-table', type: 'text', value: 'MESA {{table.name}}', bold: true },
    { id: 'kds-destinations', type: 'repeat', source: 'destinations', blocks: [
      { id: 'kds-destination', type: 'text', value: '{{name}}', bold: true },
      { id: 'kds-items', type: 'repeat', source: 'items', blocks: [{ id: 'kds-item', type: 'text', value: '{{quantity}}x {{name}}' }] },
    ] },
  ] },
  test: { version: 1, blocks: [
    { id: 'test-title', type: 'text', value: 'ALTEIL LOCAL PRINT AGENT', align: 'center', bold: true },
    { id: 'test-gap-1', type: 'spacer' },
    { id: 'test-message', type: 'text', value: 'Impresora detectada correctamente', align: 'center' },
    { id: 'test-gap-2', type: 'spacer' },
    { id: 'test-ip', type: 'row', label: 'IP', value: '{{printer.ip}}' },
    { id: 'test-port', type: 'row', label: 'Puerto', value: '{{printer.port}}' },
    { id: 'test-date', type: 'row', label: 'Fecha', value: '{{ticket.datetime}}' },
    { id: 'test-gap-3', type: 'spacer' },
    { id: 'test-complete', type: 'text', value: 'Prueba completada', align: 'center', bold: true },
  ] },
}

export function getSafeDefaultPrintTemplate(type: PrintTemplateType) {
  return structuredClone(DEFAULT_PRINT_TEMPLATES[type])
}
